import styled from '@emotion/styled';
import type { CSSProperties } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  PlanBadge,
  DropdownMenu,
  Stack,
  Badge,
  tokens,
  Text,
  Tooltip,
  Link,
} from '@hotjar/hotsauce';
import { useProductAllowances } from '@hotjar/webapp/domains/revenue/hooks/usePlanAllowances';
import { useTippy } from '@hotjar/webapp/hooks/useTippy';
import { css } from '@emotion/react';

import type {
  ProductCatalog,
  AnalyticsProduct,
  FeedbackProduct,
  Product,
} from '@hotjar/webapp/domains/common/product/types';
import { ProductType } from '@hotjar/webapp/domains/common/product/types';

import { S as StyledDropdown } from '@hotjar/webapp/features/Billing/PlanSelectDropdown/LegacyPlanSelectDropDown/PlanSelectDropdown';
import { usePricingTableByAccount } from '@hotjar/webapp/hooks/usePricingTable';
import type { SiteAccount, OwnedAccount } from '@hotjar/account';
import { getBundledPlanId } from '@hotjar/webapp/domains/common/product/utils';
import { useGetFreeTierProducts } from '@hotjar/webapp/domains/common/product/hooks/useGetFreeTierProducts';
import { usePrettifyPrice } from '@hotjar/webapp/hooks/usePrettifyPrice';
import { keysToSnakeCase } from '@hotjar/insights-common/lib/convert-string';
import { useProductComparators } from '@hotjar/webapp/domains/common/product/hooks/useProductComparators';
import { getSalesLink } from '@hotjar/upsell';
import { ProductTitle } from './styles';

const S = {
  ...StyledDropdown,
  ProductTitle,
  ActionRequiredBadge: styled(Badge)`
    background-color: ${tokens.BRAND_COLORS.neutral_900};
    margin-right: auto;
  `,
  ProductPrice: styled(Text)`
    width: 100%;
    display: flex;
    align-items: flex-start;
  `,
  DropdownMenuContainer: styled.div`
    overflow-y: scroll;
  `,
  StyledBaseItem: styled(DropdownMenu.BaseItem)`
    display: block;
    height: ${tokens.spacer.x6pt5};
    padding: ${tokens.spacer.x1} ${tokens.spacer.x1pt5};
    ${(props) => props.alternative && `background-color: ${tokens.color.bg_hover}`}
  `,
  DropdownMessage: styled(DropdownMenu.BaseItem)`
    text-align: left;
    padding: 0;
  `,
  DropdownMessageItem: styled(Text)`
    background-color: ${tokens.color.bg_info};
    padding: ${tokens.spacer.x2};
    padding-bottom: ${tokens.spacer.x5};
  `,
};

interface SkuDropdownProps {
  siteAccount: SiteAccount | OwnedAccount;
  onChange: (params: AnalyticsProduct | FeedbackProduct) => void;
  hidePlanBadges?: boolean;
  reversePlanBadges?: boolean;
  productType: keyof ProductCatalog;
  activeProduct: AnalyticsProduct | FeedbackProduct;
  products: Product[];
  showTitle?: boolean;
  isTwoSkusActionRequired?: boolean;
  style?: CSSProperties;
  currentProduct?: Product;
  disableLowerTierProducts?: boolean;
  allowBookSalesCall?: boolean;
}

function shouldDisplayBasicPlan({
  product,
  isTwoSkusActionRequired = false,
}: {
  product: AnalyticsProduct | FeedbackProduct;
  isTwoSkusActionRequired?: boolean;
}): boolean {
  return product.sku !== 'feedback_basic_20' || !isTwoSkusActionRequired;
}

export const SkuDropdown = ({
  siteAccount,
  productType,
  activeProduct,
  hidePlanBadges,
  reversePlanBadges,
  products,
  onChange,
  showTitle = true,
  isTwoSkusActionRequired,
  style,
  currentProduct,
  disableLowerTierProducts = false,
  allowBookSalesCall,
}: SkuDropdownProps): JSX.Element => {
  const { t } = useTranslation('revenue');
  const { getAllowances } = useProductAllowances();
  const { pricingTable } = usePricingTableByAccount(keysToSnakeCase(siteAccount));
  const { feedback_product: freeAsk, analytics_product: freeObserve } = useGetFreeTierProducts();
  const { prettifyPrice } = usePrettifyPrice();
  const { productLt } = useProductComparators();

  const refInstance = useTippy();

  // This flag is related to PT7 -> PT8 migration, which involved new SKU Feedback product
  //  Customer (monthly and yearly) in their grace period are prompted to choose a paid plan
  const activeActionRequired =
    isTwoSkusActionRequired &&
    activeProduct.type === ProductType.Feedback &&
    activeProduct.sku === 'feedback_basic_20';

  const firstNonSelfServeItem = products.find((product) => !product.selfServeEnabled);
  // Here we make sure to include the first non self-servable item in the list to be show
  const dropdownProductList = products.filter(
    (product) => product.selfServeEnabled || product.sku === firstNonSelfServeItem?.sku,
  );

  const renderSelectedLabel = ({ product }: { product: Product }) => {
    return product.selfServeEnabled
      ? getAllowances(product).allowanceLabel
      : t('more-than', {
          allowanceLabel: getAllowances(product).allowanceLabel,
        });
  };

  return (
    <Stack direction="column" spacing="x0pt5" style={style}>
      {showTitle && (
        <S.ProductTitle>{t(`multi-sku.${productType.toLowerCase()}.title`)}</S.ProductTitle>
      )}
      <S.StyledDropdownMenu
        onCreate={refInstance.set}
        appendTo="parent"
        trigger={
          <S.SelectContainer
            aria-label={t(`multi-sku.${productType.toLowerCase()}.title`)}
            isInvalid={false}
            isDisabled={false}
            isReadonly={false}
            tabIndex={0}
            type="button"
          >
            {!hidePlanBadges && reversePlanBadges && !activeActionRequired && (
              <S.SelectedPlanBadge kind={activeProduct.tier} size="s" />
            )}
            {!activeActionRequired && (
              <S.TruncatedLabelContainer
                data-testid={`plan-selector-active-plan-${activeProduct.sku}`}
              >
                {renderSelectedLabel({ product: activeProduct })}
              </S.TruncatedLabelContainer>
            )}
            {!hidePlanBadges && !reversePlanBadges && !activeActionRequired && (
              <S.SelectedPlanBadge kind={activeProduct.tier} size="s" />
            )}
            {!hidePlanBadges && activeActionRequired && (
              <S.ActionRequiredBadge size="s" kind="custom">
                {t('migrate-to-two-skus.action-required')}
              </S.ActionRequiredBadge>
            )}
            <S.DropdownItemIcon icon="caret_down_alt" color="text_light" />
          </S.SelectContainer>
        }
      >
        <S.DropdownMenuContainer>
          {dropdownProductList
            .filter((product) => shouldDisplayBasicPlan({ product, isTwoSkusActionRequired }))
            .map((product) => {
              const bundlePlanId = getBundledPlanId({
                analyticsPlanId:
                  product.type === ProductType.Analytics ? product.sku : freeObserve?.sku || '',
                feedbackPlanId:
                  product.type === ProductType.Feedback ? product.sku : freeAsk?.sku || '',
              });
              const price = pricingTable[bundlePlanId];
              const { selfServeEnabled } = product;

              const prettifiedPrice = price && prettifyPrice(price, 0);

              const disableProductItem =
                disableLowerTierProducts &&
                currentProduct &&
                productLt(product.sku, currentProduct?.sku);
              return (
                <S.StyledBaseItem
                  isSelected={product.sku === activeProduct.sku}
                  onClick={() => {
                    refInstance.close();
                    onChange(product);
                  }}
                  title={getAllowances(product).allowanceLabel}
                  data-testid={`selector-item-${product.sku}`}
                  isDisabled={disableProductItem}
                  key={product.sku}
                  alternative={!selfServeEnabled}
                >
                  <Tooltip
                    text={t('go-to-subscriptions')}
                    isDisabled={!disableProductItem}
                    placement="right"
                  >
                    <span>
                      <S.ItemContainer spacing="x1">
                        {!hidePlanBadges && reversePlanBadges && (
                          <PlanBadge kind={product.tier} size="s" />
                        )}
                        <S.TruncatedLabelContainer>
                          {renderSelectedLabel({ product })}
                        </S.TruncatedLabelContainer>
                        {product.sku === activeProduct.sku ? (
                          <span>
                            <S.DropdownItemIcon icon="selected" />
                          </span>
                        ) : (
                          !hidePlanBadges &&
                          !reversePlanBadges && <PlanBadge kind={product.tier} size="s" />
                        )}
                      </S.ItemContainer>
                      <S.ProductPrice textType="caption" textColor="text_light" as="span">
                        {!!pricingTable[bundlePlanId] &&
                          selfServeEnabled &&
                          t('per-month', {
                            price: prettifiedPrice,
                          })}
                        {!selfServeEnabled && t('plan-select.yearly-subscription')}
                      </S.ProductPrice>
                    </span>
                  </Tooltip>
                </S.StyledBaseItem>
              );
            })}
          {allowBookSalesCall && !firstNonSelfServeItem && (
            <S.DropdownMessage hasDivider>
              <S.DropdownMessageItem textType="caption" textColor="text_light">
                <Trans
                  i18nKey="revenue:plan-select.scale-book-a-call-message"
                  components={[
                    <Link
                      as="a"
                      isExternal
                      kind="standalone"
                      href={getSalesLink('MultiProductUpsell')}
                      css={css`
                        ${tokens.getTextStyleProperties('caption')}
                        line-height: 1;
                      `}
                    >
                      {t('book-a-call')}
                    </Link>,
                  ]}
                />
              </S.DropdownMessageItem>
            </S.DropdownMessage>
          )}
        </S.DropdownMenuContainer>
      </S.StyledDropdownMenu>
    </Stack>
  );
};
